import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "semantic-ui-react";

import { Element } from "components/ReactScroll/index";
import { createFormHelper } from "lib/Form";
import "modules/CommunicationSettings/CustomerCom.scss";

import type { DropdownItemProps } from "components";
import type { CommunicationSetting } from "models";

type RepairOverviewProps = {
  tabId: string;
  pdfTemplateOptions: DropdownItemProps[];
};

const FormHelper = createFormHelper<CommunicationSetting>();

export const RepairOverview = ({ tabId, pdfTemplateOptions }: RepairOverviewProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = FormHelper.useFormContext();

  const [repairOverviewWhenCarReady, repairOverviewWhenQualityCheck, repairOverviewWhenCarReadyAndCarOutOfShop, repairOverviewWhenCarReadyAndBilled] = watch([
    "automatically_send_repair_overview_when_car_ready",
    "automatically_send_repair_overview_when_quality_check",
    "automatically_send_repair_overview_on_car_ready_and_out_of_shop",
    "automatically_send_repair_overview_on_car_ready_and_billed"
  ]);

  const isHidePriceDisabled =
    !repairOverviewWhenCarReady && !repairOverviewWhenQualityCheck && !repairOverviewWhenCarReadyAndCarOutOfShop && !repairOverviewWhenCarReadyAndBilled;

  useEffect(() => {
    if (isHidePriceDisabled) setValue("hide_price_for_automatic_repair_order", false);
  }, [isHidePriceDisabled]);

  useEffect(() => {
    if (!repairOverviewWhenQualityCheck) setValue("disable_sending_automated_repair_overview_on_quality_issues", false);
  }, [repairOverviewWhenQualityCheck]);

  return (
    <Element name={tabId} className="customer-com-wrapper">
      <h3>{t("v8_repair_overview", "Repair Overview")}</h3>
      <div>
        <Form.Group className="mt-16">
          <Form.Field width={3}>
            <span className="items-center">
              <FormHelper.Field.Checkbox name="repair_cc_testing_mode" label={t("v8_testing_mode", "Testing mode")} />
            </span>
          </Form.Field>
          <Form.Field width={5}>
            <label>{t("v8_email", "E-mail")}</label>
            <FormHelper.Field.Input inputProps={{ fluid: true, placeholder: t("v8_email", "E-mail") }} name="repair_cc_testing_email" />
          </Form.Field>
          <Form.Field width={8}>
            <label>{t("v8_phone", "Phone")}</label>
            <FormHelper.Field.Input inputProps={{ fluid: true, placeholder: t("v8_phone", "Phone"), type: "Phone" }} name="repair_cc_testing_phone" />
          </Form.Field>
        </Form.Group>

        <Form.Group className="mt-14">
          <Form.Field width={8}>
            <label>{t("v8_welcome_text", "Welcome text")}</label>
            <FormHelper.Field.TextArea textAreaProps={{ placeholder: `${t("v8_hello", "Hello")}!` }} name="repair_overview_welcome_text" />
          </Form.Field>

          <Form.Field width={8}>
            <label>{t("v8_pdf_template", "PDF template")}</label>
            <FormHelper.Field.Dropdown
              name="report_template_id"
              options={pdfTemplateOptions}
              dropdownProps={{ placeholder: t("v8_select_template", "Select template"), selection: true }}
            />

            <FormHelper.Field.Checkbox
              formFieldProps={{ className: "mt-10" }}
              label={t("v8_automatically_send_repair_overview_on_car_ready", "Send repair overview automatically on car ready")}
              name="automatically_send_repair_overview_when_car_ready"
            />

            <FormHelper.Field.Checkbox
              formFieldProps={{ className: "mt-10" }}
              label={t("v8_automatically_send_repair_overview_on_car_ready_and_out_of_shop", "Send repair overview automatically on Car ready & Car out of shop")}
              name="automatically_send_repair_overview_on_car_ready_and_out_of_shop"
            />

            <FormHelper.Field.Checkbox
              formFieldProps={{ className: "mt-10" }}
              name="automatically_send_repair_overview_on_car_ready_and_billed"
              label={t("v8_automatically_send_repair_overview_on_car_ready_and_billed", "Send repair overview automatically on Car Ready + Billed")}
            />

            <FormHelper.Field.Checkbox
              formFieldProps={{ className: "mt-10" }}
              name="automatically_send_repair_overview_when_quality_check"
              label={t("v8_automatically_send_repair_overview_on_quality_check", "Send repair overview automatically on quality check")}
            />

            <FormHelper.Field.Checkbox
              formFieldProps={{ className: "mt-10" }}
              name="disable_sending_automated_repair_overview_on_quality_issues"
              label={t("v8_dont_send_automated_repair_overview_if_there_are_quality_issues", "Don't send automated repair overview if there are quality issues")}
              disabled={!repairOverviewWhenQualityCheck}
            />

            <FormHelper.Field.Checkbox
              formFieldProps={{ className: "mt-10" }}
              name="hide_price_for_automatic_repair_order"
              label={t("v8_hide_price_automated_repair_overview_emails", "Hide price on automated repair overview emails")}
              disabled={isHidePriceDisabled}
            />
          </Form.Field>
        </Form.Group>
      </div>
    </Element>
  );
};
